import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { axiosInstance } from '@/api/client';

interface AutomationStatus {
  isRunning: boolean;
  lastRunTime: string | null;
  retryCount: number;
  timestamp: string;
}

interface AutomationResult {
  readyToShipUpdated: number;
  shippedUpdated: number;
  deliveredArchived: number;
}

export default function AutomationPage() {
  const [status, setStatus] = useState<AutomationStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const { toast } = useToast();

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/admin/automation-status');
      const data = response.data;
      
      if (data.status === 'success') {
        setStatus(data.data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch automation status',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to connect to automation service',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const triggerAutomation = async () => {
    setTriggering(true);
    try {
      const response = await axiosInstance.post('/admin/trigger-automation');
      const data = response.data;
      
      if (data.status === 'success') {
        const result: AutomationResult = data.data;
        toast({
          title: 'Automation Completed',
          description: `Updated ${result.readyToShipUpdated} ready-to-ship orders, ${result.shippedUpdated} shipped orders, and archived ${result.deliveredArchived} delivered orders.`,
        });
        // Refresh status after successful trigger
        await fetchStatus();
      } else {
        toast({
          title: 'Error',
          description: data.message || 'Failed to trigger automation',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to trigger automation',
        variant: 'destructive',
      });
    } finally {
      setTriggering(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto-refresh status every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Order Automation Control</h1>
        <Button 
          onClick={fetchStatus} 
          disabled={loading}
          variant="outline"
        >
          {loading ? 'Refreshing...' : 'Refresh Status'}
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Status Card */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Automation Status</h2>
          {status ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="font-medium">Status:</span>
                <span className={`px-2 py-1 rounded text-sm ${
                  status.isRunning 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {status.isRunning ? 'Running' : 'Idle'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Last Run:</span>
                <span className="text-sm">{formatDate(status.lastRunTime)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Retry Count:</span>
                <span>{status.retryCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Last Updated:</span>
                <span className="text-sm">{formatDate(status.timestamp)}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              {loading ? 'Loading status...' : 'Failed to load status'}
            </div>
          )}
        </Card>

        {/* Control Card */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Manual Control</h2>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Manually trigger the order automation process to update order statuses:
            </p>
            <ul className="text-sm space-y-1 text-gray-600">
              <li>• Ready to Ship → Shipped (after 2 days)</li>
              <li>• Shipped → Delivered (after 2 weeks)</li>
              <li>• Archive old delivered orders (keep 500 most recent)</li>
            </ul>
            <Button 
              onClick={triggerAutomation}
              disabled={triggering || status?.isRunning}
              className="w-full"
              size="lg"
            >
              {triggering ? 'Triggering...' : 
               status?.isRunning ? 'Automation Running...' : 
               'Trigger Automation'}
            </Button>
          </div>
        </Card>
      </div>

      {/* Schedule Information */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Automatic Schedule</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <h3 className="font-medium mb-2">Daily Run</h3>
            <p className="text-sm text-gray-600">Every day at 2:00 AM</p>
          </div>
          <div>
            <h3 className="font-medium mb-2">Frequent Updates</h3>
            <p className="text-sm text-gray-600">Every 6 hours</p>
          </div>
        </div>
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> The automation runs automatically on schedule. 
            Manual triggering is useful for testing or immediate processing.
          </p>
        </div>
      </Card>
    </div>
  );
}
